import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { UserCreateBody } from "../api/models/user.models";
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { Company } from "../api/models/companies.models";
import { v4 as uuidv4 } from "uuid";
import { useRolesForSelect } from "../utils/roleUtils";
import { Checkbox } from "primereact/checkbox";
import usePermissions from "../hooks/usePermissions";
import { CreatePermissionsBody } from "../api/models/permission.models";
import { InfoDot } from "../components/infoDot";

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding: 0;
  }
  .form-container {
    padding: 0 2rem;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  gap: 0.25rem;
  & .p-inputtext,
  .p-password {
    width: 100%;
  }

  & .p-password > div {
    width: 100%;
  }
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 1;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  padding-left: 1rem;
  gap: 0.4rem;
`;

const CheckBoxWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

interface CreateUserDialogProps {
  show: boolean;
  onHide: () => void;
  createNewUser: (
    data: UserCreateBody & { companyId: number; organizationId?: number },
  ) => Promise<any>;
  savingNewUser: boolean;
  sendingUserInvite: boolean;
  sendUserInvite: ({
    userId,
    companyId,
    organizationId,
  }: {
    userId: number;
    companyId: number;
    organizationId?: number;
  }) => Promise<any>;
  companies: Company[];
  preselectedCompany?: Company | null;
  preselectedOrganization?: { id: number; name: string } | null;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  show,
  onHide,
  createNewUser,
  savingNewUser,
  sendingUserInvite,
  sendUserInvite,
  companies,
  preselectedCompany,
  preselectedOrganization,
}) => {
  const { t } = useTranslation();
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(
    preselectedCompany || null,
  );
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [roleIds, setRoleIds] = React.useState<number[]>([100]);

  const onCompanyRoleChange = (newRoleIds: number[]) => {
    const updated = [...newRoleIds];
    setRoleIds(updated);
  };

  const { grantPermissions, isGrantingPermissions, error } = usePermissions({
    userId: undefined as any,
    companyId: selectedCompany?.id,
    organizationId: preselectedOrganization?.id,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UserCreateBody>({
    defaultValues: {
      names: { firstName: "", lastName: "", name: "" },
      email: { email: "", isValidated: false },
      phoneNumber: { number: "", isValidated: false },
      password: "",
    },
    mode: "onBlur",
  });

  const roleOptions = useRolesForSelect();
  const sortedRoleOptions = [...roleOptions].sort((a, b) => {
    // Put role with value 100 first
    if (a.value === 100) return -1;
    if (b.value === 100) return 1;
    return 0;
  });

  const getFormErrorMessage = (name: string) => {
    // @ts-ignore
    return errors[name] ? (
      <small className="p-error">{errors[name]?.message}</small>
    ) : null;
  };

  const onSubmit = async (data: UserCreateBody) => {
    const newRolesWithoutRead = roleIds.filter((id) => id !== 100); // Exclude 'Read' role
    const newRoles = newRolesWithoutRead.includes(900)
      ? [900] // If 'Company Admin' is selected, only assign that
      : newRolesWithoutRead;

    const result = await createNewUser({
      ...data,
      companyId: selectedCompany!.id,
      organizationId: preselectedOrganization?.id,
      password: uuidv4().slice(0, 8),
    });

    const inviteResult = await sendUserInvite({
      userId: result.id,
      companyId: selectedCompany!.id,
      organizationId: preselectedOrganization?.id,
    });

    if (result && inviteResult) {
      if (newRoles.length > 0 && !!result.id) {
        const createPermissionBody: CreatePermissionsBody = {
          userId: result?.id,
          companyId: selectedCompany!.id,
          permissions: newRoles,
          organizationId: preselectedOrganization?.id,
        };
        const permissionResult = await grantPermissions(createPermissionBody);
        if (permissionResult) {
          reset();
          setRoleIds([100]);
          onHide();
        }
      }

      reset();
      setRoleIds([100]);
      onHide();
    }
  };

  const handleCompanyChange = (event: any) => {
    const companyId = event.value;
    setSelectedCompany(companyId);
  };

  const search = (event: AutoCompleteCompleteEvent) => {
    // Timeout to emulate a network connection
    setTimeout(() => {
      let _filteredCompanies;

      if (!event.query.trim().length) {
        _filteredCompanies = [...(companies || [])];
      } else {
        _filteredCompanies = companies?.filter((company) => {
          return company.companyName
            .toLowerCase()
            .startsWith(event.query.toLowerCase());
        });
      }

      setFilteredCompanies(_filteredCompanies);
    }, 250);
  };

  const isCompanyAdmin = roleIds.includes(900);

  return (
    <StyledDialog
      header={t("header-create-user")}
      visible={show}
      style={{ width: "500px", minHeight: "400px" }}
      onHide={onHide}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-container">
          {!preselectedCompany && (
            <InputContainer>
              <label htmlFor="company">{t("label-select-user-company")}</label>
              <AutoComplete
                id="company"
                value={selectedCompany}
                suggestions={filteredCompanies}
                dropdown
                completeMethod={search}
                field="companyName"
                onChange={handleCompanyChange}
              />
            </InputContainer>
          )}
          <InputContainer>
            <label htmlFor="firstName">{t("label-first-name")}</label>
            <Controller
              name="names.firstName"
              control={control}
              rules={{ required: t("label-required") }}
              render={({ field }) => <InputText id="firstName" {...field} />}
            />
            {getFormErrorMessage("names.firstName")}
          </InputContainer>
          <InputContainer>
            <label htmlFor="lastName">{t("label-last-name")}</label>
            <Controller
              name="names.lastName"
              control={control}
              rules={{ required: t("label-required") }}
              render={({ field }) => <InputText id="lastName" {...field} />}
            />
            {getFormErrorMessage("names.lastName")}
          </InputContainer>
          <InputContainer>
            <label htmlFor="email">{t("label-email")}</label>
            <Controller
              name="email.email"
              control={control}
              rules={{ required: t("label-required") }}
              render={({ field }) => <InputText id="email" {...field} />}
            />
            {getFormErrorMessage("email.email")}
          </InputContainer>
          <InputContainer>
            <label htmlFor="phoneNumber">{t("label-phone-number")}</label>
            <Controller
              name="phoneNumber.number"
              control={control}
              rules={{ required: t("label-required") }}
              render={({ field }) => <InputText id="phoneNumber" {...field} />}
            />
            {getFormErrorMessage("phoneNumber.number")}
          </InputContainer>

          <CheckBoxWrapper>
            <CheckboxContainer>
              {sortedRoleOptions.map((role) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    gap: "0.6rem",
                  }}
                  key={role.value}
                >
                  <Checkbox
                    id={`role-${role.value}`}
                    checked={
                      roleIds.includes(role.value as number) || isCompanyAdmin
                    }
                    onChange={(e) => {
                      const newRoleIds = e.target.checked
                        ? [...roleIds, role.value as number]
                        : roleIds.filter((id) => id !== role.value);
                      onCompanyRoleChange(newRoleIds);
                    }}
                    disabled={
                      (isCompanyAdmin && role.value !== 900) ||
                      role.value === 100
                    }
                  />
                  <label htmlFor={`role-${role.value}`}>{role.label}</label>
                </div>
              ))}
            </CheckboxContainer>
            <InfoDot
              text=""
              content={
                <div>
                  <p>{t("infotext-permissions-header")}</p>
                  <p>
                    <strong>{t("label-admin")}:</strong>{" "}
                    {t("infotext-permissions-admin")}
                  </p>
                  <p>
                    <strong>{t("label-read")}:</strong>{" "}
                    {t("infotext-permissions-read")}
                  </p>
                  <p>
                    <strong>{t("label-write")}:</strong>{" "}
                    {t("infotext-permissions-write")}
                  </p>
                  <p>
                    <strong>{t("label-report-reader")}:</strong>{" "}
                    {t("infotext-permissions-report-reader")}
                  </p>
                  <p>
                    <strong>{t("label-report-creator")}:</strong>{" "}
                    {t("infotext-permissions-report-creator")}
                  </p>
                </div>
              }
            >
              <div />
            </InfoDot>
          </CheckBoxWrapper>
        </div>
        <DialogActions>
          <Button
            type="button"
            label={t("cmd-cancel")}
            className="p-button-text"
            onClick={onHide}
          />
          <Button
            type="submit"
            label={t("cmd-save")}
            className="p-ml-2"
            disabled={
              savingNewUser ||
              sendingUserInvite ||
              (!selectedCompany && !preselectedCompany)
            }
            loading={
              savingNewUser || sendingUserInvite || isGrantingPermissions
            }
          />
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default CreateUserDialog;
